/**
 * product-engraving.js
 *
 * Wires up the [data-engraving-block] elements rendered by
 * blocks/product-engraving.liquid:
 *   - Smooth checkbox expand/collapse panel
 *   - ATC interception: when engraving is on, fire a SINGLE /cart/add.js
 *     with TWO items so Shopify's add succeeds atomically:
 *       1) main variant with line item properties[Ražba] = TEXT
 *       2) Ražba fee variant (qty matches main)
 *
 * Why external asset (not inline script in the block):
 *   - Shopify section renderer can strip inline scripts during re-render.
 *   - Horizon CSP / Web Component lifecycle may not execute inline JS.
 *   - External asset is always evaluated once per page load, plus
 *     MutationObserver picks up blocks injected by theme editor / drawers.
 *
 * Horizon ATC architecture (verified in assets/product-form.js):
 *   <product-form-component on:submit="/handleSubmit"> wraps the <form>.
 *   Horizon's framework binds handleSubmit at the component level via
 *   bubble phase. Our document-level CAPTURE phase listener runs FIRST
 *   and uses stopImmediatePropagation to fully take over.
 */
// EARLY BANNER — fires even if IIFE later errors, so we always know the file loaded.
console.warn('[engraving] file loaded at', new Date().toISOString());
window.__engravingDebug = { fileLoaded: true, blocks: 0, wired: 0, intercepts: 0 };

(function () {
  'use strict';
  var DEBUG = true;
  function log() {
    if (DEBUG) console.warn.apply(console, ['[engraving]'].concat([].slice.call(arguments)));
  }

  function wireBlock(root) {
    if (!root || root.dataset.engravingWired === '1') return;
    root.dataset.engravingWired = '1';

    var formId = root.getAttribute('data-engraving-form-id');
    var razbaVariantId = root.getAttribute('data-engraving-variant-id');
    var propertyKey = root.getAttribute('data-engraving-key') || 'Ražba';
    var checkbox = root.querySelector('[data-engraving-toggle]');
    var input = root.querySelector('[data-engraving-input]');

    if (!checkbox || !input) { log('missing controls, skip', root); return; }
    log('wire', { formId: formId, razbaVariantId: razbaVariantId, propertyKey: propertyKey });
    window.__engravingDebug.wired++;
    root.setAttribute('data-engraving-debug-wired', 'yes');

    function sync() {
      var on = checkbox.checked;
      root.classList.toggle('engraving-block--open', on);
      input.disabled = !on;
      checkbox.setAttribute('aria-expanded', on ? 'true' : 'false');
      if (on) {
        input.value = (input.value || '').toUpperCase();
        setTimeout(function () { try { input.focus(); } catch (e) {} }, 220);
      } else {
        input.value = '';
      }
    }
    checkbox.addEventListener('change', sync);
    input.addEventListener('input', function () { input.value = input.value.toUpperCase(); });

    if (!razbaVariantId) { log('no razba variant id, abort'); return; }

    function shouldIntercept(targetForm) {
      if (!targetForm) return false;
      // <form> has named-property access: form.id refers to the child
      // <input name="id"> (the variant id), NOT the form's id attribute.
      // Use getAttribute to read the real DOM id.
      var realFormId = targetForm.getAttribute && targetForm.getAttribute('id');
      if (realFormId !== formId) return false;
      if (!checkbox.checked) return false;
      if (!input.value || !input.value.trim()) return false;
      return true;
    }

    // Find every <cart-items-component> on the page (cart page + cart drawer)
    // and collect their data-section-id so /cart/add.js returns updated section
    // HTML in the JSON response — Horizon's drawer swaps it in via cart:update.
    function getCartSectionIds() {
      var ids = [];
      var comps = document.querySelectorAll('cart-items-component[data-section-id]');
      comps.forEach(function (c) {
        var id = c.dataset.sectionId;
        if (id && ids.indexOf(id) === -1) ids.push(id);
      });
      return ids.join(',');
    }

    function dispatchCartUpdate(response) {
      // Horizon's <cart-drawer-component> listens for ThemeEvents.cartUpdate
      // ('cart:update'). detail.data.sections is the {section_id: html} object
      // the cart drawer uses to swap rendered HTML — exactly mirrors what
      // product-form.js does in handleSubmit when it dispatches CartAddEvent.
      try {
        var evt = new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: response,
            sourceId: 'product-engraving-block',
            data: {
              source: 'product-form-component',
              sections: response && response.sections ? response.sections : undefined,
              itemCount: response && response.item_count
            }
          }
        });
        document.dispatchEvent(evt);
        window.dispatchEvent(evt);
      } catch (e) { log('dispatchCartUpdate failed', e); }
    }

    function dualAdd(targetForm) {
      var fd = new FormData(targetForm);
      var qty = parseInt(fd.get('quantity') || '1', 10) || 1;
      var mainId = parseInt(fd.get('id'), 10);
      var razbaId = parseInt(razbaVariantId, 10);
      var props = {};
      props[propertyKey] = input.value.trim().toUpperCase();
      var sections = getCartSectionIds();

      log('dual ATC', { mainId: mainId, razbaId: razbaId, qty: qty, props: props, sections: sections });

      var body = {
        items: [
          { id: mainId, quantity: qty, properties: props },
          { id: razbaId, quantity: qty }
        ]
      };
      if (sections) body.sections = sections;

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, status: r.status, body: j }; }); })
        .then(function (res) {
          log('cart/add.js response', { status: res.status, item_count: res.body && res.body.item_count, sections: res.body && res.body.sections ? Object.keys(res.body.sections) : null });
          if (!res.ok) throw new Error((res.body && (res.body.description || res.body.message)) || ('HTTP ' + res.status));

          // Reset the engraving UI so the user can engrave another pair later.
          checkbox.checked = false;
          sync();

          // Refresh cart UI BEFORE opening drawer so it shows the new items.
          // Horizon's <cart-drawer-component> listens for cart:update and swaps
          // sections from detail.data.sections — no extra fetch needed.
          dispatchCartUpdate(res.body);

          // Open the drawer AFTER the section-swap event so it renders the
          // updated cart content immediately, not an empty stale view.
          setTimeout(function () {
            var drawer = document.querySelector('cart-drawer-component, cart-drawer');
            if (drawer) {
              try {
                if (typeof drawer.open === 'function') drawer.open();
                else if (typeof drawer.showModal === 'function') drawer.showModal();
                else drawer.removeAttribute('hidden');
              } catch (e) { log('drawer open failed', e); }
            }
          }, 50);
        })
        .catch(function (err) {
          console.error('[engraving] dual ATC failed', err);
          alert('Nepodařilo se přidat do košíku: ' + err.message);
        });
    }

    // (1) Submit event (Horizon's <product-form-component on:submit="/handleSubmit">)
    document.addEventListener('submit', function (e) {
      var form = e.target;
      window.__engravingDebug.submitsSeen = (window.__engravingDebug.submitsSeen || 0) + 1;
      window.__engravingDebug.lastSubmitFormId = form && form.getAttribute && form.getAttribute('id');
      if (!shouldIntercept(form)) {
        log('submit seen but not matching', { actualId: form && form.getAttribute && form.getAttribute('id'), expectedId: formId, checked: checkbox.checked, value: input.value });
        return;
      }
      log('intercept submit', form.getAttribute('id'));
      window.__engravingDebug.intercepts++;
      e.preventDefault();
      e.stopImmediatePropagation();
      dualAdd(form);
    }, true);

    // (2) Click on submit button (backup path)
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('button[type="submit"], [type="submit"]') : null;
      if (!btn) return;
      var form = btn.closest('form');
      if (!shouldIntercept(form)) return;
      log('intercept click', btn);
      e.preventDefault();
      e.stopImmediatePropagation();
      dualAdd(form);
    }, true);
  }

  function wireAll() {
    var blocks = document.querySelectorAll('[data-engraving-block]');
    log('found blocks:', blocks.length);
    window.__engravingDebug.blocks = blocks.length;
    blocks.forEach(wireBlock);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wireAll);
  } else {
    wireAll();
  }

  // Theme editor / variant change can re-render the block — observe & re-wire.
  try {
    var mo = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var m = mutations[i];
        for (var j = 0; j < m.addedNodes.length; j++) {
          var n = m.addedNodes[j];
          if (!(n instanceof HTMLElement)) continue;
          if (n.matches && n.matches('[data-engraving-block]')) wireBlock(n);
          var nested = n.querySelectorAll ? n.querySelectorAll('[data-engraving-block]') : [];
          nested.forEach(wireBlock);
        }
      }
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  } catch (e) { log('MutationObserver failed', e); }

  log('engraving controller loaded');
})();
